/*
 * HappyLife: suggesties in de cart drawer.
 *
 * Doet vier dingen:
 * 1. kleurknopjes (goud/zilver/...) omschakelen, inclusief prijs en foto
 * 2. een variant toevoegen zonder de drawer te verlaten
 * 3. de lijst aanvullen met Shopify-aanbevelingen als de gekozen collecties
 *    te weinig overhouden
 * 4. de pijlen links/rechts tonen, verbergen en laten scrollen
 *
 * De drawer wordt bij elke cart-wijziging opnieuw opgebouwd (zie
 * getSectionsToRender in cart-drawer.js en cart.js). Dit element wordt dan
 * opnieuw aangemaakt en connectedCallback draait vanzelf weer.
 */
class CartUpsell extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.list = this.querySelector('.cart-upsell__list');
    this.status = this.querySelector('.cart-upsell__status');
    this.navButtons = this.querySelectorAll('.cart-upsell__nav');
    this.limit = parseInt(this.dataset.limit, 10) || 8;

    this.addEventListener('click', this.onClick.bind(this));

    if (this.list) {
      this.updateNav = this.updateNav.bind(this);
      this.list.addEventListener('scroll', this.updateNav, { passive: true });

      /* Meet opnieuw zodra de drawer breder/smaller wordt of de kaarten hun
         definitieve hoogte krijgen (lazy geladen foto's). */
      if (typeof ResizeObserver === 'function') {
        this.observer = new ResizeObserver(this.updateNav);
        this.observer.observe(this.list);
      }
    }

    this.topUpWithRecommendations();
    this.updateNav();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  onClick(event) {
    const swatch = event.target.closest('.cart-upsell__swatch');
    if (swatch) {
      this.selectVariant(swatch);
      return;
    }

    const nav = event.target.closest('.cart-upsell__nav');
    if (nav) {
      this.scrollStrip(nav.dataset.direction);
      return;
    }

    const addButton = event.target.closest('button.cart-upsell__add');
    if (addButton) {
      event.preventDefault();
      this.addToCart(addButton);
    }
  }

  /* Een kaartbreedte plus de tussenruimte opschuiven, zodat de strook netjes
     op een kaartrand uitkomt in plaats van halverwege een foto. */
  scrollStrip(direction) {
    if (!this.list) return;

    const card = this.list.querySelector('.cart-upsell__item');
    const gap = parseFloat(getComputedStyle(this.list).columnGap) || 11;
    const step = card ? card.getBoundingClientRect().width + gap : this.list.clientWidth * 0.8;

    this.list.scrollBy({ left: direction === 'prev' ? -step : step, behavior: 'smooth' });
  }

  /*
   * Pijl verbergen zodra er in die richting niets meer te halen valt. De marge
   * van 2px vangt afrondingsverschillen op: sommige browsers komen bij een
   * volledig doorgescrolde strook net een fractie te kort.
   */
  updateNav() {
    if (!this.list || !this.navButtons || this.navButtons.length === 0) return;

    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    const position = this.list.scrollLeft;

    this.navButtons.forEach((button) => {
      const isPrev = button.dataset.direction === 'prev';
      button.hidden = isPrev ? position <= 2 : position >= maxScroll - 2;
    });
  }

  /* Kleurknopje aanklikken: selectie, prijs en de variant van de toevoegknop bijwerken. */
  selectVariant(swatch) {
    const item = swatch.closest('.cart-upsell__item');
    if (!item) return;

    item.querySelectorAll('.cart-upsell__swatch').forEach((option) => {
      const isChosen = option === swatch;
      option.classList.toggle('is-selected', isChosen);
      option.setAttribute('aria-pressed', isChosen ? 'true' : 'false');
    });

    const addButton = item.querySelector('button.cart-upsell__add');
    if (addButton) {
      addButton.dataset.variantId = swatch.dataset.variantId;
      this.resetButton(addButton);
    }

    const priceNow = item.querySelector('.cart-upsell__price-now');
    if (priceNow && swatch.dataset.price) priceNow.textContent = swatch.dataset.price;

    const priceWas = item.querySelector('.cart-upsell__price-was');
    if (priceWas) {
      const compareAt = swatch.dataset.compareAtPrice;
      priceWas.textContent = compareAt || '';
      priceWas.hidden = !compareAt;
    }

    this.swapImage(item, swatch);
  }

  /*
   * Foto van het kaartje meewisselen met de gekozen kleur.
   *
   * De nieuwe foto wordt eerst apart ingeladen; pas als hij klaar is wisselen
   * we hem om. Zetten we de src meteen, dan staat het kaartje even leeg terwijl
   * het bestand binnenkomt. De teller op het kaartje zorgt dat bij snel achter
   * elkaar klikken alleen de laatste keuze het beeld nog aanpast.
   */
  swapImage(item, swatch) {
    const image = item.querySelector('.cart-upsell__image');
    const source = swatch.dataset.src;
    if (!image || !source || image.getAttribute('src') === source) return;

    const token = (Number(item.dataset.swapToken) || 0) + 1;
    item.dataset.swapToken = token;

    const apply = () => {
      if (Number(item.dataset.swapToken) !== token) return;
      image.removeAttribute('loading');
      image.srcset = swatch.dataset.srcset || '';
      image.src = source;
    };

    const preloader = new Image();
    if (swatch.dataset.srcset) preloader.srcset = swatch.dataset.srcset;
    preloader.sizes = image.sizes || '';
    preloader.src = source;

    if (preloader.complete) {
      apply();
      return;
    }

    preloader.addEventListener('load', apply, { once: true });
    preloader.addEventListener('error', apply, { once: true });

    /* Noodrem: blijft het laden hangen, dan wisselen we alsnog. */
    setTimeout(apply, 1200);
  }

  addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.hasAttribute('aria-busy')) return;

    const item = button.closest('.cart-upsell__item');
    const label = button.querySelector('.cart-upsell__add-label');

    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) throw new Error(data.description || data.message || data.status);

        if (label) label.textContent = 'Toegevoegd';
        button.classList.add('is-added');
        if (this.status && item) {
          const title = item.querySelector('.cart-upsell__title');
          this.status.textContent = `${title ? title.textContent.trim() : 'Product'} toegevoegd aan je winkelwagen.`;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-upsell',
            productVariantId: variantId,
            cartData: data,
          });
        }

        /*
         * De drawer opnieuw laten opbouwen door het thema zelf. Dat ververst in
         * één keer de regels, de gratis-verzendbalk, het totaal en deze lijst.
         * We wachten even zodat "Toegevoegd" nog leesbaar is.
         */
        const drawer = document.querySelector('cart-drawer');
        if (drawer && data.sections) {
          setTimeout(() => drawer.renderContents(data), 600);
        } else {
          window.location.reload();
        }
      })
      .catch((error) => {
        console.error('cart-upsell:', error);
        if (label) label.textContent = 'Niet gelukt';
        button.classList.add('is-error');
        setTimeout(() => this.resetButton(button), 2500);
      })
      .finally(() => {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
      });
  }

  resetButton(button) {
    const label = button.querySelector('.cart-upsell__add-label');
    if (label) label.textContent = 'Toevoegen';
    button.classList.remove('is-added', 'is-error');
  }

  /*
   * Aanvullen met Shopify-aanbevelingen zodra de gekozen collectie te weinig
   * producten overhoudt. Levert dat ook niets op, dan verbergen we het blok
   * in plaats van een lege kop te tonen.
   */
  topUpWithRecommendations() {
    if (!this.list) return;

    const alreadyShown = this.list.children.length;
    const productId = this.dataset.productId;

    if (alreadyShown >= this.limit || !productId) {
      this.hidden = alreadyShown === 0;
      this.updateNav();
      return;
    }

    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');

        const seen = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
        this.list.querySelectorAll('[data-product-handle]').forEach((shown) => {
          seen.add(shown.dataset.productHandle);
        });

        parsed.querySelectorAll('.cart-upsell__item').forEach((candidate) => {
          if (this.list.children.length >= this.limit) return;
          const handle = candidate.dataset.productHandle;
          if (!handle || seen.has(handle)) return;
          seen.add(handle);
          this.list.appendChild(candidate);
        });
      })
      .catch((error) => {
        console.error('cart-upsell:', error);
      })
      .finally(() => {
        this.hidden = this.list.children.length === 0;
        this.updateNav();
      });
  }
}

customElements.define('cart-upsell', CartUpsell);
